import './InspectionPart.css';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addLack } from '../../../redux/actions';

import PropTypes from 'prop-types';
import { getValueFromStatus, raisedButtonRowStyles, getStyleValue } from '../../Helpers/Helpers';
import { useHasError } from '../../FormView/FormView';

import ActionInput from '../../ActionInput/ActionInput';
import YearInput from '../../YearInput/YearInput';
import TextArea from '../../TextArea/TextArea';
import BrandTypeInput from '../../BrandTypeInput/BrandTypeInput';
import { LackEntry } from '../../LacksInput/LacksInput';
import { DetailsItem } from '../../Details/Details';
import { ScoreInput } from '../../ScoreInput/ScoreInput.jsx';

import {
	IconButton,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Button,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import {
	Add,
	ExpandMore,
} from '@mui/icons-material';

function InspectionPart({ part, errorElements = [] }) {
	const buttonStyles = raisedButtonRowStyles();
	const buttonSize = '36.5px'; // 36.0 and 37.0 are a pixel off for proper alignment of scoreInput, ActionInput and toggler

	const {
		id,
		name,
		action,
		previous_action,
		modification_year,
		status,
		previous_status,
		description,
		previous_description,
		installation_part,
	} = part;

	const hasError = useHasError(errorElements);
	const { t, knownLacks } = useSelector((redux) => redux.store);
	const dispatch = useDispatch();
	const display_description =
		description === null ? previous_description : description;

	const [modificationYear, setModificationYear] = useState(modification_year);
	const [expanded, setExpanded] = useState(false);
	const [hasComment, setHasComment] = useState(!!display_description);
	const [lacks, setLacks] = useState(part.lacks);

	// Update the inspection object every time state of lacks changes
	useEffect(() => {
		part.lacks = lacks;
	}, [ lacks ]);

	/**
	 * Parameter can be event or value; typeof will evaluate to object or string
	 * @param  {Event | string} param
	 */
	function handleTextAreaChange(param) {
		if (typeof param === 'object') {
			setHasComment(!!param.target.value);
		} else {
			setHasComment(false);
		}
	}

	/**
	 * Modifies modificationYear
	 * NOTE: this is only used as a display value, the value that is sent
	 * back to the backend is what is in the YearInput’s own state (for legacy
	 * reasons)
	 * @param  {Event} event
	 */
	function handleYearInputChange(event) {
		if (event.target.value && event.target.value.length === 4) {
			setModificationYear(event.target.value);
		} else {
			setModificationYear(modification_year);
		}
	}

	/**
	 * Modifies lacks state. Because we don’t have ID's we simply iterate
	 * over all lacks and if the current iteration’s array index matches
	 * the new lack’s index, store the new lack in that index position
	 *
	 * @param  {Object} updatedLack
	 * @param  {Number} updatedLackIndex
	 */
	function handleLackUpdate(updatedLack, updatedLackIndex) {
		setLacks(lacks.map((lack, index) => {
			return index === updatedLackIndex
				? updatedLack
				: lack;
		}));
	}

	/**
	 * For lack that doesn’t have an id (doesn’t exist on the server yet):
	 * Removes a lack from the part.lacks array. No index is needed, we can
	 * find the lack using indexOf. We clone the existing lacks because we
	 * don’t want to modify the inspection part directly
	 *
	 * Lacks that already have an id, will get a "delete_me" property which
	 * is used to render a 'will be deleted' state, and triggers actual
	 * deletion on the BFF
	 *
	 * @param  {Object} lack
	 */
	function handleLackDelete(lack) {
		if (lack.id) {
			lack.delete_me = true;

			// Because the lacks property of the inspection object is only
			// changed after the lacks state updates, we simply clone the
			// current lacks and update lacks state with it.
			const clonedLacks = [ ...part.lacks ];
			setLacks(clonedLacks);
		} else {
			const clonedLacks = [ ...part.lacks ];
			const lackIndex = part.lacks.indexOf(lack);
			clonedLacks.splice(lackIndex, 1);
			setLacks(clonedLacks);
		}
	}

	/**
	 * Store an empty lack object in state. This will render a new LackInput
	 */
	function handleClickAdd() {
		setLacks([ ...lacks, {} ]);
	}

	/**
	 * When a new lack is created, add it to knownLacks so other parts can use
	 * it too; it will be persisted to localstorage for repeat sessions
	 *
	 * @param  {String} title
	 */
	function handleLackCreate(title) {
		dispatch(addLack({
			title,
			part_id: part.id,
		}));
	}

	return (
		<div className="generate-part">
			<input type="hidden" name={ `${id}_installation_part` } value={ installation_part } />
			<Grid
				className="inspection-part__container"
				sx={{ marginBottom: '.5rem' }}
				container>
				<Grid item xs={3}>
					<Typography
						variant="h6" component="h2">
						{name}
					</Typography>
					<Typography
						component="div"
						variant="subtitle1"
						color='text.secondary'
					>
						{ modificationYear }
					</Typography>
				</Grid>
				<Grid item xs={9}>
					<Accordion expanded={expanded} disableGutters={true} >
						<Stack direction="row">
							<AccordionSummary
								className="inspection-part__accordion-summary"
								expandIcon={
									<IconButton
										className={ hasComment ? 'inspection-part__accordion-button--has-comment' : '' }
										onClick={() => setExpanded(!expanded)}
										style={{
											...buttonStyles['style'],
											color: getStyleValue('color-text'),
											backgroundColor: getStyleValue('color-grey'),
											boxShadow: 'var(--shadow-mui-clone)',
											width: buttonSize,
											height: buttonSize,
											border: 0,
										}}
									>
										<ExpandMore />
									</IconButton>
								}
							>
								<div className='inspection-part__inputs'>
									<ScoreInput
										name={`${id}_status`}
										previousScore={getValueFromStatus(previous_status)}
										score={getValueFromStatus(status)}
										error={hasError(`${id}_status`, t.generate_part_score_error)}
									/>

									<ActionInput
										data-notrequired
										id={`${id}_action`}
										name={`${id}_action`}
										t={t}
										defaultSelected={getValueFromStatus(previous_action)}
										selected={getValueFromStatus(action)}
									/>
								</div>
							</AccordionSummary>
						</Stack>
						<AccordionDetails>
							<TextArea
								data-notrequired
								className="text-area"
								name={`${id}_description`}
								triggerid={`${id}-trigger`}
								value={display_description}
								onChange={ handleTextAreaChange }
								floatingLabelText={t.notes}
							/>
							<DetailsItem
								noMargin>
								<YearInput
									data-notrequired
									className="year-input spacing-default-right"
									id={`${id}_modification_year`}
									name={`${id}_modification_year`}
									defaultValue={''}
									onChange={ handleYearInputChange }
									value={ modificationYear ? modificationYear.toString() : '' }
									t={t}
								/>
							</DetailsItem>

							<BrandTypeInput part={part} />

							<div className="lacks-input">
								<Divider />

								<Typography
									variant="subtitle1">
									{ t.nen_add_lacks_header || 't.nen_add_lacks_header' }
								</Typography>

								{ lacks.length > 0 && lacks.map((lack, index) =>
									<LackEntry
										key={index}
										lack={lack}
										options={knownLacks.filter(({ part_id }) => Number(id) === Number(part_id))}
										onChangeLack={(lack) => handleLackUpdate(lack, index)}
										onDeleteLack={handleLackDelete}
										onCreateLack={handleLackCreate}
									/>,
								)}

								<Button
									className="lacks-input__add-button"
									variant='text'
									startIcon={<Add/>}
									onClick={ handleClickAdd }
								>
									{t.action_add}
								</Button>
							</div>
						</AccordionDetails>
					</Accordion>
				</Grid>
			</Grid>
		</div>
	);
}

InspectionPart.propTypes = {
	parts: PropTypes.object,
	activeView: PropTypes.string,
};

export default InspectionPart;
